/**
 * Published Reports — dashboard content (publish-report spec, Req 5, 6).
 *
 * Left: the reports visible to the reader in the current team (global, team
 * public, or their own), newest first, filterable All / Mine / Team / Global.
 * Right: the selected report — title, description, provenance, question and
 * the answer rendered with the chat's own renderer (marked + AnswerTableDownloads,
 * so tables keep their Download CSV control) — plus Copy link / Edit /
 * Unpublish for readers who may manage it.
 *
 * Content is fetched only for the selected report (the list document leaves
 * `content` unselected). `?report=<id>` is honoured on mount and written on
 * selection so a report view is bookmarkable (Req 5.6, 5.7).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { marked, parse } from "marked";
import { generateAuditedClient } from "../../../../audit/auditedClient";
import {
  getPublishedReport,
  getPublishedReportsSummary,
} from "../../../../graphql/queries";
import { unpublishReport } from "../../../../graphql/mutations";
import type {
  GetPublishedReportsInput,
  PublishedReport,
  PublishedReportSummary,
  UnpublishReportInput,
} from "../../../../models/API";
import type { PluginReportContentProps } from "../../types";
import AnswerTableDownloads from "../../../csvDownload/AnswerTableDownloads";
import ConfirmationDialog from "../../../utils/ConfirmationDialog";
import { covertUtcToLocal } from "../../../utils/formatters";
import {
  canManagePublishedReport,
  modelLabel,
  parsePublishedReportSource,
  publishedReportDeepLink,
  visibilityOf,
  VISIBILITY_STYLE,
} from "../../../publishedReports/publishedReportModel";
import EditPublishedReportDialog from "./EditPublishedReportDialog";

marked.setOptions({ breaks: true, gfm: true });

type Filter = "all" | "mine" | "team" | "global";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "team", label: "Team" },
  { id: "global", label: "Global" },
];

function readReportIdFromUrl(): number | null {
  const raw = new URLSearchParams(window.location.search).get("report");
  const id = raw ? Number(raw) : NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
}

function writeReportIdToUrl(id: number | null) {
  const params = new URLSearchParams(window.location.search);
  if (id === null) params.delete("report");
  else params.set("report", String(id));
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}?${params.toString()}`
  );
}

function errorMessageOf(err: any, fallback: string): string {
  const message = err?.errors?.[0]?.message ?? err?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

const PublishedReportsContent: React.FC<PluginReportContentProps> = ({
  effectiveRole,
  currentTeamId,
  userId,
}) => {
  const client = useMemo(() => generateAuditedClient(), []);
  const [reports, setReports] = useState<PublishedReportSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [selectedId, setSelectedId] = useState<number | null>(() =>
    readReportIdFromUrl()
  );
  const [detail, setDetail] = useState<PublishedReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMissing, setDetailMissing] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const ready = userId !== undefined && currentTeamId !== undefined;

  const loadList = useCallback(async () => {
    if (!ready) return;
    setListLoading(true);
    setListError(null);
    try {
      const input: GetPublishedReportsInput = {
        user_id: userId!,
        team_id: currentTeamId!,
      };
      const result = await client.graphql({
        query: getPublishedReportsSummary,
        variables: { input },
      });
      setReports(result.data.getPublishedReports ?? []);
    } catch (err) {
      console.error("[PublishedReports] list failed:", err);
      setListError(
        errorMessageOf(
          err,
          "Could not load published reports. Please try again."
        )
      );
    } finally {
      setListLoading(false);
    }
  }, [client, ready, userId, currentTeamId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Detail fetch (content included) for the selected report, inside the scope.
  useEffect(() => {
    if (!ready || selectedId === null) {
      setDetail(null);
      setDetailMissing(false);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetailMissing(false);
    async function loadDetail() {
      try {
        const input: GetPublishedReportsInput = {
          user_id: userId!,
          team_id: currentTeamId!,
          report_id: selectedId!,
        };
        const result = await client.graphql({
          query: getPublishedReport,
          variables: { input },
        });
        if (cancelled) return;
        const found = (result.data.getPublishedReports ?? [])[0] ?? null;
        setDetail(found);
        setDetailMissing(!found);
      } catch (err) {
        console.error("[PublishedReports] detail failed:", err);
        if (!cancelled) {
          setDetail(null);
          setDetailError(
            errorMessageOf(err, "Could not load this report. Please try again.")
          );
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    }
    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [client, ready, userId, currentTeamId, selectedId]);

  const handleSelect = (id: number) => {
    setSelectedId(id);
    writeReportIdToUrl(id);
  };

  const visibleReports = reports.filter((r) => {
    if (filter === "mine") return r.creator_id === userId;
    if (filter === "team")
      return r.is_public && !r.is_global && r.team_id === currentTeamId;
    if (filter === "global") return r.is_global;
    return true;
  });

  const canManage =
    detail !== null &&
    canManagePublishedReport(detail, { userId, effectiveRole, currentTeamId });

  const handleCopyLink = async () => {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(publishedReportDeepLink(detail));
      setNotice("Link copied to clipboard");
    } catch (err) {
      console.error("[PublishedReports] clipboard copy failed:", err);
      setNotice(
        "Could not copy the link. Please copy it from the address bar."
      );
    }
  };

  const handleUnpublish = async () => {
    setConfirmUnpublish(false);
    if (!detail || userId === undefined) return;
    try {
      const input: UnpublishReportInput = { id: detail.id, user_id: userId };
      const result = await client.graphql({
        query: unpublishReport,
        variables: { input },
      });
      if (!result.data.unpublishReport) throw new Error("No report returned");
      setNotice("Report unpublished");
      setSelectedId(null);
      writeReportIdToUrl(null);
      await loadList();
    } catch (err) {
      console.error("[PublishedReports] unpublish failed:", err);
      setNotice(errorMessageOf(err, "Could not unpublish this report."));
    }
  };

  const handleEdited = (updated: PublishedReportSummary) => {
    setEditOpen(false);
    setDetail((prev) => (prev ? { ...prev, ...updated } : prev));
    setReports((prev) =>
      prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
    );
    setNotice("Report updated");
  };

  const renderMeta = (report: PublishedReport) => {
    const source = parsePublishedReportSource(report.source);
    const scope = source.request_scope ?? null;
    const tools = source.tools_used ?? [];
    return (
      <Typography variant="caption" color="text.secondary" component="div">
        Published by <strong>{report.creator ?? report.creator_id}</strong> in{" "}
        <strong>{report.team_name ?? report.team_id}</strong> on{" "}
        {covertUtcToLocal(report.published_at)}. Model:{" "}
        <strong>{modelLabel(scope?.model_id)}</strong>
        {scope?.team_id !== undefined && scope?.team_id !== null && (
          <>
            {" "}
            · asked under team <strong>{scope.team_id}</strong>
          </>
        )}
        {" · Tools: "}
        <strong>{tools.length > 0 ? tools.join(", ") : "none recorded"}</strong>
        {!scope &&
          " · Provenance: unknown (answered before provenance stamping)"}
      </Typography>
    );
  };

  return (
    <Box
      sx={{ display: "flex", gap: 2, minHeight: 480 }}
      data-testid="published-reports"
    >
      {/* List */}
      <Box sx={{ width: 340, minWidth: 280, flexShrink: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <Chip
                key={f.id}
                label={f.label}
                size="small"
                color={filter === f.id ? "primary" : "default"}
                variant={filter === f.id ? "filled" : "outlined"}
                onClick={() => setFilter(f.id)}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Stack>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={loadList}
                disabled={listLoading}
                aria-label="refresh reports"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {listError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {listError}
          </Alert>
        )}
        {listLoading && reports.length === 0 ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading reports…</Typography>
          </Box>
        ) : visibleReports.length === 0 && !listError ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {reports.length === 0
              ? "No published reports yet. Publish one from any completed Ask AI answer."
              : "No reports match this filter."}
          </Typography>
        ) : (
          <List
            dense
            disablePadding
            sx={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            {visibleReports.map((r) => {
              const style = VISIBILITY_STYLE[visibilityOf(r)];
              return (
                <ListItemButton
                  key={r.id}
                  selected={r.id === selectedId}
                  onClick={() => handleSelect(r.id)}
                  sx={{
                    borderLeft: `4px solid ${style.borderColor}`,
                    backgroundColor: style.backgroundColor,
                    borderRadius: 1,
                    mb: 0.5,
                    alignItems: "flex-start",
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ flex: 1 }}
                        >
                          {r.title}
                        </Typography>
                        <Chip
                          size="small"
                          label={style.chipLabel}
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      </Stack>
                    }
                    secondary={`${r.creator ?? r.creator_id} · ${
                      r.team_name ?? r.team_id
                    } · ${covertUtcToLocal(r.published_at)}`}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      <Divider orientation="vertical" flexItem />

      {/* Detail */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {notice && (
          <Alert severity="info" onClose={() => setNotice(null)} sx={{ mb: 1 }}>
            {notice}
          </Alert>
        )}
        {selectedId === null && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Select a report to read it.
          </Typography>
        )}
        {selectedId !== null && detailLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading report…</Typography>
          </Box>
        )}
        {selectedId !== null && !detailLoading && detailError && (
          <Alert severity="error">{detailError}</Alert>
        )}
        {selectedId !== null &&
          !detailLoading &&
          !detailError &&
          detailMissing && (
            <Alert severity="warning">
              This report is not available to you or was unpublished.
            </Alert>
          )}
        {detail && !detailLoading && (
          <Box>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ wordBreak: "break-word" }}
                >
                  {detail.title}
                </Typography>
                {detail.description && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {detail.description}
                  </Typography>
                )}
                <Box sx={{ mt: 0.5 }}>{renderMeta(detail)}</Box>
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Chip
                  size="small"
                  label={VISIBILITY_STYLE[visibilityOf(detail)].chipLabel}
                  variant="outlined"
                />
                <Tooltip title="Copy link">
                  <IconButton
                    size="small"
                    onClick={handleCopyLink}
                    aria-label="Copy report link"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canManage && (
                  <>
                    <Tooltip title="Edit title, description or visibility">
                      <IconButton
                        size="small"
                        onClick={() => setEditOpen(true)}
                        aria-label="Edit report"
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Unpublish">
                      <IconButton
                        size="small"
                        onClick={() => setConfirmUnpublish(true)}
                        aria-label="Unpublish report"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2" color="text.secondary">
              Question
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
              {detail.question}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              Answer
            </Typography>
            <Box
              className="ChatBoxComponent-message AI"
              sx={{ p: 1.5, borderRadius: 2 }}
            >
              <AnswerTableDownloads
                html={(parse(detail.content ?? "") as string).replace(
                  />\s+</g,
                  "><"
                )}
                messageIndex={0}
                conversationId={`published-report-${detail.id}`}
              />
            </Box>
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        isOpen={confirmUnpublish}
        onConfirm={handleUnpublish}
        onClose={() => setConfirmUnpublish(false)}
        message="Unpublish this report? It will disappear from every list; the original conversation is not affected."
      />
      {detail && (
        <EditPublishedReportDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          report={detail}
          userId={userId ?? 0}
          effectiveRole={effectiveRole}
          onSaved={handleEdited}
        />
      )}
    </Box>
  );
};

export default PublishedReportsContent;
